import superagent from "superagent/dist/superagent";
import isPlainObject from "lodash/isPlainObject";
import dispatchWindowEvent from "./dispatchWindowEvent";
import debounce from "lodash/debounce";

// global __HOST__, localStorage, FileList, File
const BASE_URL = "/app/v1";

function flatten(target) {
  const output = {};

  function step(object, prev) {
    if (Array.isArray(object) && object.length) {
      object.forEach((item, idx) => step(item, prev ? `${prev}[${idx}]` : `[${idx}]`));
      return;
    }
    if (isPlainObject(object) && Object.keys(object).length) {
      Object.keys(object).forEach(key => step(object[key], prev ? `${prev}.${key}` : key));
      return;
    }
    output[prev] = object;
  }

  if (isPlainObject(target) && Object.keys(target).length) {
    step(target);
  }

  return output;
}

function parseResponseError(error) {
  let errorMessage;
  if (error.response) {
    if (error.response.body && typeof error.response.body === "object") {
      errorMessage = error.response.body;
      /* eslint-disable no-underscore-dangle */
      if (errorMessage.non_field_errors) {
        errorMessage._error = errorMessage.non_field_errors.join(" ");
        delete errorMessage.non_field_errors;
      }
      if (errorMessage.__all__) {
        errorMessage._error = errorMessage.__all__.join(" ");
        delete errorMessage.__all__;
      }
      /* eslint-enable no-underscore-dangle */
    } else if (error.response.body) {
      errorMessage = { _error: error.response.body };
    } else if (error.response.text) {
      errorMessage = { _error: error.response.text };
    } else {
      errorMessage = {
        _error: error.response.error && error.response.error.message,
      };
    }
  } else {
    errorMessage = {
      _error:
        "Your request could not be finished because there was an error with your internet connection. Please check your internet connection. Your data was ot saved yet.",
    };
  }

  Object.keys(errorMessage).forEach(key => {
    if (Array.isArray(errorMessage[key])) {
      errorMessage[key] = errorMessage[key].join(" ");
    }
  });

  return errorMessage;
}

const singleRequestState = {};

async function pureFetch({
  url,
  method = "get",
  query = {},
  data = undefined,
  fields = {},
  attachments = {},
  onProgress,
  external = false,
  useNativeError = false,
  isRealUrl = false,
  ensureURLSingleRequest = false,
}) {
  if (!url) {
    throw new Error("url required!");
  }
  const realUrl = isRealUrl ? url : `${BASE_URL}${url}`;
  let request = superagent[method](realUrl);

  request = request.query(query);

  if (isPlainObject(data) || Array.isArray(data)) {
    request = request.send(data);
  }

  if (isPlainObject(fields) && Object.keys(fields).length) {
    const flattenFields = flatten(fields);

    Object.keys(flattenFields)
      .filter(fieldName => flattenFields[fieldName])
      .forEach(fieldName => {
        if (flattenFields[fieldName] instanceof FileList) {
          Array.prototype.forEach.call(flattenFields[fieldName], oFile => {
            request = request.attach(fieldName, oFile);
          });
        } else if (flattenFields[fieldName] instanceof File) {
          request = request.attach(fieldName, flattenFields[fieldName]);
        } else {
          request = request.field(fieldName, flattenFields[fieldName]);
        }
      });
  }

  if (isPlainObject(attachments) && Object.keys(attachments).length) {
    Object.keys(attachments)
      .filter(attachmentName => attachments[attachmentName])
      .forEach(attachmentName => {
        request = request.attach(attachmentName, attachments[attachmentName]);
      });
  }

  // const token = localStorage.getItem('token');
  // request = request.set('Authorization', token ? `token ${token}` : undefined);

  request = request.set("Accept", "application/json");

  if (ensureURLSingleRequest) {
    const prevRequests = singleRequestState[url];
    if (prevRequests && prevRequests.length !== 0) {
      prevRequests.forEach(r => r.abort());
    }
    if (!singleRequestState[url]) {
      singleRequestState[url] = [request];
    } else {
      singleRequestState[url].push(request);
    }
  }

  if (!external) {
    request.withCredentials();
  }
  if (onProgress) {
    request.on("progress", e => onProgress(e));
  }

  try {
    const response = await request;
    if (window.REQUEST_MOCK) {
      console.log("Request MOCK", JSON.stringify({ url, query, data, response }));
    }
    return response.body;
    // ({ successCallback, errorCallback, error: undefined, response });
  } catch (error) {
    const errorObj = parseResponseError(error);
    if (error?.response?.statusCode === 401) {
      dispatchEvent401(errorObj);
    }
    if (useNativeError) {
      throw new Error(errorObj._error);
    } else {
      return Promise.reject(errorObj);
    }
  } finally {
    if (ensureURLSingleRequest) {
      singleRequestState[url] = singleRequestState[url].filter(r => r !== request);
    }
  }
}

const dispatchEvent401 = debounce(
  errorObj => dispatchWindowEvent("API_RESPONSE_401", errorObj),
  200
);

export default {
  async get(config) {
    return pureFetch({ ...config, method: "get" });
  },

  async post(config) {
    return pureFetch({ ...config, method: "post" });
  },

  async put(config) {
    return pureFetch({ ...config, method: "put" });
  },

  async patch(config) {
    return pureFetch({ ...config, method: "patch" });
  },

  async del(config) {
    return pureFetch({ ...config, method: "del" });
  },
};
